import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LANDING_PAGE_AUDIT } from 'Pages/TeamDashboard/CreateReview/components/constants';

import { saveFlowAction } from 'Redux/Actions/productLibraryActions';

import { captureLandingPage, getCaptureErrorMessage } from 'services/landingPageCapture';

import { FLOW_SOURCE } from 'utlis/constants';

const INVALID_URL_MESSAGE = 'Enter a valid website URL, e.g. acme.com';
const SAVE_FLOW_ERROR_MESSAGE = 'Failed to save flow. Please try again.';

// Prepends https:// when no scheme was typed; returns null unless the result is a plausible
// public http(s) URL. Whether the host is actually public is enforced by the AI server.
export const normalizeLandingPageUrl = rawUrl => {
  const trimmed = (rawUrl || '').trim();

  if (!trimmed) {
    return null;
  }

  const withScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    // eslint-disable-next-line compat/compat -- IE11 not in browserslist
    const parsed = new URL(withScheme);

    if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname.includes('.')) {
      return null;
    }

    return withScheme;
  }
  catch {
    return null;
  }
};

const isAbortError = err => err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';

/**
 * Captures a landing page via the AI server and saves the screenshot as a single-screen
 * product library flow, returning it in the same shape ProductLibrarySelection hands to
 * CreateReview's onConfirm so the rest of the audit flow is unchanged.
 */
const useLandingPageCapture = workspaceUuid => {
  const dispatch = useDispatch();
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);
  // Last successful capture: { url (normalized), flow, images }
  const [captureResult, setCaptureResult] = useState(null);
  const abortControllerRef = useRef(null);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsCapturing(false);
  }, []);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const clearError = useCallback(() => setError(null), []);

  const captureAndCreateFlow = useCallback(async rawUrl => {
    const url = normalizeLandingPageUrl(rawUrl);

    if (!url) {
      setError(INVALID_URL_MESSAGE);

      return null;
    }

    abortControllerRef.current?.abort();

    // eslint-disable-next-line compat/compat -- IE11 not in browserslist
    const controller = new AbortController();

    abortControllerRef.current = controller;
    setError(null);
    setCaptureResult(null);
    setIsCapturing(true);

    try {
      let capture;

      try {
        capture = await captureLandingPage(url, controller.signal);
      }
      catch (err) {
        if (!isAbortError(err) && !controller.signal.aborted) {
          setError(getCaptureErrorMessage(err));
        }

        return null;
      }

      if (controller.signal.aborted) {
        return null;
      }

      let savedFlow;

      try {
        savedFlow = await dispatch(saveFlowAction(workspaceUuid, {
          name: LANDING_PAGE_AUDIT.FLOW_NAME,
          source: FLOW_SOURCE.SCREENSHOT,
          category_name: LANDING_PAGE_AUDIT.FLOW_CATEGORY,
          assets: [{ image_url: capture.screenshotUrl, order: 1 }],
        }));
      }
      catch {
        if (!controller.signal.aborted) {
          setError(SAVE_FLOW_ERROR_MESSAGE);
        }

        return null;
      }

      const result = {
        url,
        flow: {
          uuid: savedFlow?.uuid,
          name: LANDING_PAGE_AUDIT.FLOW_NAME,
          category_name: LANDING_PAGE_AUDIT.FLOW_CATEGORY,
          current_version: 1,
          selected_version: 1,
          source: FLOW_SOURCE.SCREENSHOT,
          landing_page_url: capture.finalUrl,
          assets: [{ image_url: capture.screenshotUrl, order: 1 }],
        },
        images: [{
          uid: `library-landing-${savedFlow?.uuid || capture.captureId}`,
          url: capture.screenshotUrl,
          preview: capture.screenshotUrl,
          isLibraryImage: true,
          progress: 100,
          isProgressLoading: false,
        }],
      };

      // The flow exists now even if the user switched away mid-save — keep it either way.
      setCaptureResult(result);

      return result;
    }
    finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
        setIsCapturing(false);
      }
    }
  }, [dispatch, workspaceUuid]);

  return {
    captureAndCreateFlow, abort, isCapturing, error, clearError, captureResult,
  };
};

export default useLandingPageCapture;
